import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { getEditColumnsConfig } from '@root/src/modules/global/service/editable-column/editable-column.service';
import { CustomField } from 'core/service/custom-fields/custom-field.type';
import { DEFAULT_LONG_TIMEOUT_SLOW_REQUEST } from 'core/constants/constants';
import { AppGlobalContext, AppGlobalContextType } from 'core/contexts/app.provider.context';
import { useCustomFields } from 'core/contexts/custom-fields/use-custom-fields.hook';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { useTranslation } from 'core/i18n/i18n';
import { useContext, useEffect } from 'react';
import { usePopupManager } from 'react-popup-manager';

import {
    SettingsLoadingContainer,
    SettingsViewContent,
    SettingsViewHeader,
    SettingsViewLayout,
    TitleWithOptions,
} from 'modules/settings/settings.style';
import { CustomFieldModalComponent } from './commons/custom-field-modal.component';
import { SettingsSystemCustomFieldsComponent } from './settings-system-custom-fields.component';

export const SettingsSystemCustomFieldsContainer: React.FC = () => {
    const { setIsPageLoading } = useContext<AppGlobalContextType>(AppGlobalContext);
    const { setUserPreferencesViewConfig } = useUserPreferencesContext();
    const { customFields, refreshCustomFields, isLoading, createCustomField, updateCustomField, deleteCustomField } =
        useCustomFields();

    const popupManager = usePopupManager();
    const { t } = useTranslation();

    useEffect(() => {
        refreshCustomFields();
    }, []);

    const refreshUserPreferences = () => {
        setIsPageLoading(true);

        const handleEditColumnsConfig = (result: Awaited<ReturnType<typeof getEditColumnsConfig>>) => {
            setUserPreferencesViewConfig(result.userPreferences);
        };

        const handleEditColumnsConfigFinally = () => {
            setIsPageLoading(false);
        };

        const fetchEditColumnsConfig = () => {
            getEditColumnsConfig().then(handleEditColumnsConfig).finally(handleEditColumnsConfigFinally);
        };

        setTimeout(fetchEditColumnsConfig, DEFAULT_LONG_TIMEOUT_SLOW_REQUEST);
    };

    const openCustomFieldModal = (customField: CustomField) => {
        popupManager.open(CustomFieldModalComponent, {
            customField,
            afterActions: refreshUserPreferences,
            createCustomField,
            updateCustomField,
            deleteCustomField,
        });
    };

    return (
        <SettingsViewLayout>
            <SettingsViewHeader>
                <TitleWithOptions>
                    <PocTypography element="h5" variant="title5">
                        {t('settings.system.custom-fields.title')}
                    </PocTypography>
                    <PocButton
                        variant="secondary"
                        size="medium"
                        elevated
                        onClick={() =>
                            openCustomFieldModal({
                                name: '',
                                order: Math.max(...customFields.map((c) => c.order), 0) + 1,
                                type: 'OPERATORS',
                            })
                        }
                    >
                        {t('settings.system.custom-fields.actions.new')}
                    </PocButton>
                </TitleWithOptions>
                <PocTypography element="p" variant="body1">
                    {t('settings.system.custom-fields.description')}
                </PocTypography>
            </SettingsViewHeader>
            <SettingsViewContent>
                {isLoading ? (
                    <SettingsLoadingContainer>
                        <PocProgressSpinner variant="primary" size="large" />
                    </SettingsLoadingContainer>
                ) : (
                    <SettingsSystemCustomFieldsComponent
                        customFields={customFields}
                        onClickCustomFieldOptions={openCustomFieldModal}
                    />
                )}
            </SettingsViewContent>
        </SettingsViewLayout>
    );
};
